import { useState } from 'react';
import { client } from '@/api/client';
import { toast } from 'sonner';
import { X, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import StepTaxForms from '@/components/onboarding/StepTaxForms';

// Self-service W-4 (Company Driver) / W-9 (Owner Operator) completion for
// a driver who never filled theirs in at onboarding (or whose record
// predates onboarding capturing it at all). Reuses the exact same
// StepTaxForms fields onboarding itself uses, so the two flows can never
// drift on what's asked or how it's validated -- only the save target
// differs (a straight DriverProfile.update here instead of the
// onboarding wizard's one-shot final submit).
export default function CompleteTaxFormModal({ driver, onClose, onSaved }) {
  const isOwnerOperator = driver.driver_type === 'Owner Operator';
  const [form, setForm] = useState({
    w4_ssn: driver.w4_ssn || '', w4_filing_status: driver.w4_filing_status || '',
    w4_multiple_jobs: driver.w4_multiple_jobs || false,
    w4_dependents_qualifying: driver.w4_dependents_qualifying || '',
    w4_dependents_other: driver.w4_dependents_other || '', w4_other_credits: driver.w4_other_credits || '',
    w4_other_income: driver.w4_other_income || '', w4_deductions: driver.w4_deductions || '',
    w4_extra_withholding: driver.w4_extra_withholding || '',
    w4_exempt: driver.w4_exempt || false, w4_signature: driver.w4_signature || '',
    w9_business_name: driver.w9_business_name || '', w9_tax_classification: driver.w9_tax_classification || '',
    w9_llc_classification: driver.w9_llc_classification || '',
    w9_tin_type: driver.w9_tin_type || 'SSN', w9_tin: driver.w9_tin || '', w9_signature: driver.w9_signature || '',
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const set = (key, value) => {
    setForm(f => ({ ...f, [key]: value }));
    setErrors(e => (e[key] ? { ...e, [key]: undefined } : e));
  };

  // Same rules Onboarding.jsx applies to this same step (see its
  // validateStep for step 7) -- kept in sync deliberately since a driver
  // completing this later should face the identical bar as one who filled
  // it in during onboarding.
  const validate = () => {
    const errs = {};
    if (isOwnerOperator) {
      if (!form.w9_tax_classification) errs.w9_tax_classification = 'Required';
      if (!form.w9_tin || form.w9_tin.replace(/\D/g, '').length < 9) errs.w9_tin = 'Enter a valid SSN or EIN';
      if (!form.w9_signature) errs.w9_signature = 'Signature is required';
    } else {
      if (!form.w4_ssn || form.w4_ssn.replace(/\D/g, '').length !== 9) errs.w4_ssn = 'Enter a valid 9-digit SSN';
      if (!form.w4_exempt && !form.w4_filing_status) errs.w4_filing_status = 'Select a filing status';
      if (!form.w4_signature) errs.w4_signature = 'Signature is required';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) { toast.error('Please fix the highlighted fields.'); return; }
    setSaving(true);
    try {
      await client.entities.DriverProfile.update(driver.id, isOwnerOperator ? {
        w9_business_name: form.w9_business_name,
        w9_tax_classification: form.w9_tax_classification,
        w9_llc_classification: form.w9_llc_classification,
        w9_tin_type: form.w9_tin_type,
        w9_tin: form.w9_tin,
        w9_signature: form.w9_signature,
      } : {
        w4_ssn: form.w4_ssn,
        w4_filing_status: form.w4_exempt ? '' : form.w4_filing_status,
        w4_multiple_jobs: form.w4_exempt ? false : form.w4_multiple_jobs,
        w4_dependents_qualifying: form.w4_exempt ? 0 : form.w4_dependents_qualifying,
        w4_dependents_other: form.w4_exempt ? 0 : form.w4_dependents_other,
        w4_other_credits: form.w4_exempt ? 0 : form.w4_other_credits,
        w4_other_income: form.w4_exempt ? 0 : form.w4_other_income,
        w4_deductions: form.w4_exempt ? 0 : form.w4_deductions,
        w4_extra_withholding: form.w4_exempt ? 0 : form.w4_extra_withholding,
        w4_exempt: form.w4_exempt,
        w4_signature: form.w4_signature,
      });
      toast.success(isOwnerOperator ? 'W-9 submitted.' : 'W-4 submitted.');
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.message || 'Could not save your form.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 overflow-y-auto">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-xl my-8 p-6 space-y-5 z-10 bg-background border border-border rounded-2xl shadow-lg">
        <div className="flex items-center justify-between">
          <h2 className="font-space font-semibold text-foreground text-lg">Complete Your {isOwnerOperator ? 'W-9' : 'W-4'}</h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="w-5 h-5" /></button>
        </div>

        <StepTaxForms form={form} set={set} errors={errors} driverType={driver.driver_type} />

        <div className="flex gap-3 pt-1">
          <Button onClick={handleSubmit} disabled={saving} className="flex-1 bg-primary text-primary-foreground">
            {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</> : 'Submit'}
          </Button>
          <Button onClick={onClose} variant="outline" className="border-border">Cancel</Button>
        </div>
      </div>
    </div>
  );
}
